import { useEffect, useMemo, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import { openConsoleLink, type CollabEditorMountOptions, type CollabEditorSource } from '@nimbalyst/collab-bundle/editor';
import { getCollabDocsSession } from '@nimbalyst/collab-bundle/docs-ui';
import type { TeamJwt, TeamSession } from '../auth/teamJwt';
import { orgDocumentPath } from '../routing';
import { InlineCollabDocument } from './InlineCollabDocument';
import { webCollabScopeKey } from './WebCollabHost';
import { resolveDecisionArtifact, resolveEmbedFilePath, type DocsSnapshot } from './embedResolution';

type Snapshot = DocsSnapshot;
type EmbedRequest = Parameters<NonNullable<CollabEditorMountOptions['renderFileEmbed']>>[0];
type PreviewSession = Pick<TeamSession, 'orgId' | 'memberId'>;

export { resolveDecisionArtifact, resolveEmbedFilePath };

const DEFAULT_EMBED_HEIGHT_PX = 400;
const MIN_EMBED_HEIGHT_PX = 120;
const MIN_EMBED_WIDTH_PX = 200;

function px(value: string | undefined, min: number): number | null {
  const parsed = value ? parseInt(value, 10) : NaN;
  return Number.isNaN(parsed) ? null : Math.max(parsed, min);
}

/** The desktop `EmbedFrameShell`'s sizing: `height` defaults to 400, an unset `width` fills the column. */
export function embedPreviewSize(attrs: Readonly<Record<string, string>>): CSSProperties {
  const width = px(attrs.width, MIN_EMBED_WIDTH_PX);
  return {
    height: px(attrs.height, MIN_EMBED_HEIGHT_PX) ?? DEFAULT_EMBED_HEIGHT_PX,
    ...(width ? { width, maxWidth: '100%' } : {}),
  };
}

function useDocsSnapshot(orgId: string, projectId: string): Snapshot | null | 'no-session' {
  const [snapshot, setSnapshot] = useState<Snapshot | null | 'no-session'>(null);
  useEffect(() => {
    let active = true;
    const dataSource = getCollabDocsSession(webCollabScopeKey(orgId, projectId))?.dataSource;
    if (!dataSource) {
      setSnapshot('no-session');
      return;
    }
    const read = () => { void dataSource.snapshot().then((value) => { if (active) setSnapshot(value); }).catch(() => { if (active) setSnapshot(null); }); };
    read();
    const unsubscribe = dataSource.subscribe(read);
    return () => { active = false; unsubscribe(); };
  }, [orgId, projectId]);
  return snapshot;
}

/** Opens a console link in this tab through the editor's console link opener; a modified click keeps the browser's behavior. */
function openInConsole(event: MouseEvent<HTMLAnchorElement>) {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (openConsoleLink(event.currentTarget.href)) event.preventDefault();
}

/**
 * Resolve against the host document's authorized index, never local disk paths.
 *
 * Two callers: a decision's artifact (no `embed`, a fixed preview box), and an
 * embed block in a page (`embed` carries the link title, which sizes the box
 * and names the open link). `resolve` turns the snapshot into a document id.
 */
export function DecisionArtifactPreview({ artifact, hostDocumentId, projectId, teamSession, getTeamJwt, embed, readOnly = true, resolve }: {
  artifact: string; hostDocumentId: string; projectId: string; teamSession: PreviewSession;
  getTeamJwt: () => Promise<TeamJwt>;
  embed?: { label: string; attrs: Readonly<Record<string, string>> };
  /** An embed follows its page: editable when the page is, as the server allows. */
  readOnly?: boolean;
  resolve?: (snapshot: Snapshot) => string | null;
}) {
  const snapshot = useDocsSnapshot(teamSession.orgId, projectId);
  const ready = snapshot && snapshot !== 'no-session' ? snapshot : null;
  const documentId = ready ? (resolve ? resolve(ready) : resolveDecisionArtifact(ready, artifact, teamSession.orgId, hostDocumentId)) : null;
  const size = useMemo(() => (embed ? embedPreviewSize(embed.attrs) : { height: 320 }), [embed]);
  if (!documentId) {
    return <div className="decision-artifact-unavailable">
      {snapshot ? `${embed?.label || 'This artifact'} is not available in this shared project.` : 'Opening shared artifact…'}
    </div>;
  }
  const href = orgDocumentPath({ orgId: teamSession.orgId }, projectId, documentId);
  // An interactive HTML page is as tall as its content unless the link says otherwise.
  const fitsContent = embed && !embed.attrs.height && ready?.items.find((item) => item.documentId === documentId)?.documentType === 'html';
  return <div className="decision-artifact-preview" data-testid="document-embed-preview" style={embed ? { width: size.width, maxWidth: size.maxWidth } : undefined}>
    <div className="flex flex-col overflow-hidden" style={fitsContent ? undefined : { height: size.height }}>
      <InlineCollabDocument documentId={documentId} projectId={projectId} teamSession={teamSession} getTeamJwt={getTeamJwt} readOnly={readOnly} />
    </div>
    {embed
      ? <a href={new URL(href, window.location.origin).href} onClick={openInConsole}>Open {embed.label || 'page'}</a>
      : <a href={href} target="_blank" rel="noopener">Open artifact in a browser tab</a>}
  </div>;
}

/**
 * The embed renderers a team page's editor mounts with: a shared-document
 * embed (`nimbalyst://doc/...`) and a file link beside the page
 * (`[Map](Product%20Map.excalidraw)`) both render the linked page live at the
 * size its link title asks for. Null for a source that is not a team room.
 */
export function useTeamDocumentEmbeds(source: CollabEditorSource, readOnly: boolean | undefined): Pick<CollabEditorMountOptions, 'renderDecisionArtifact' | 'renderFileEmbed'> | null {
  const room = source.kind === 'team-room' ? source : null;
  const orgId = room?.room.orgId;
  const projectId = room?.room.projectId;
  const hostDocumentId = room?.room.documentId;
  const memberId = room?.auth.memberId;
  const getTeamJwt = room?.auth.getTeamJwt;
  return useMemo(() => {
    if (!orgId || !projectId || !hostDocumentId || !memberId || !getTeamJwt) return null;
    const teamSession = { orgId, memberId } as PreviewSession;
    const preview = (artifact: string, embed: { label: string; attrs: Readonly<Record<string, string>> } | undefined, resolve?: (snapshot: Snapshot) => string | null): ReactNode => (
      <DecisionArtifactPreview artifact={artifact} hostDocumentId={hostDocumentId} projectId={projectId} teamSession={teamSession} getTeamJwt={getTeamJwt} embed={embed} readOnly={embed ? readOnly : true} resolve={resolve} />
    );
    return {
      renderDecisionArtifact: (_entryId, artifact, embed) => preview(artifact, embed),
      renderFileEmbed: ({ src, label, attrs }: EmbedRequest) => preview(src, { label, attrs }, (snapshot) => resolveEmbedFilePath(snapshot, src, hostDocumentId)),
    };
  }, [orgId, projectId, hostDocumentId, memberId, getTeamJwt, readOnly]);
}
